import React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import { useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import NavLink from './NavLink';
import './navigation.css';
import { LoadedUser } from '../../interfaces';
import { ROUTES } from '../../constants/global';
import useGetUnreadRequests from '../../hooks/requests/useGetUnreadRequests';
import useBusiness from '../../hooks/businesses/useBusiness';
import { isUserAdminOrManager } from '../../utils/global/helper';

interface Props {
  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  loadedUser: LoadedUser | undefined;
}

function SideNav({ mobileDrawerOpen, setMobileDrawerOpen, loadedUser }: Props) {
  const { isIndependent } = useBusiness();

  const isDesktop = useMediaQuery('(min-width:900px)');
  const path = useLocation().pathname;

  const { data: unreadRequestsMap } = useGetUnreadRequests({
    listen: true,
  });
  const unreadRequests = Object.values(unreadRequestsMap || {}).flat();

  const links = [
    {
      name: 'Calendar',
      route: '/calendar',
      icon: '/calendar.svg',
      visible: true,
    },
    {
      name: 'Booking Requests',
      route: '/requests',
      icon: '/notification.svg',
      visible: true,
      badgeCount: unreadRequests?.length || 0,
    },
    {
      name: 'Marketing',
      route: ROUTES.marketing,
      icon: '/marketing.svg',
      visible:
        !!loadedUser &&
        (isIndependent || isUserAdminOrManager(loadedUser)) &&
        loadedUser.canManageCampaigns,
    },
    {
      name: 'Customers',
      route: '/customers',
      icon: '/group.svg',
      visible: true,
    },
    {
      name: 'Reports',
      route: '/reports',
      icon: '/graph.svg',
      visible: !!loadedUser?.canViewReports,
    },
    {
      name: 'Checkout',
      route: '/checkouts',
      icon: '/money.svg',
      visible: true,
    },
    {
      name: 'Waivers',
      route: ROUTES.waivers,
      icon: '/waivers.svg',
      visible: true,
    },
  ];

  const filteredLinks = links.filter(link => link.visible);

  const drawerWidth = isDesktop ? 264 : '100%';

  return (
    <>
      <Drawer
        variant="permanent"
        className="desktop-side-nav"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}>
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List className="nav-links">
            {filteredLinks.map(link => (
              <NavLink
                linkName={link.name}
                linkRoute={link.route}
                isActive={!!link.route && path.includes(link.route)}
                icon={link.icon}
                badgeCount={link.badgeCount}
                key={link.name}
              />
            ))}
          </List>
        </Box>
      </Drawer>
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}>
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List className="nav-links">
            {filteredLinks.map(link => (
              <NavLink
                linkName={link.name}
                linkRoute={link.route}
                isActive={!!link.route && path.includes(link.route)}
                icon={link.icon}
                key={link.name}
                onNavigate={() => {
                  setMobileDrawerOpen(false);
                }}
                badgeCount={link.badgeCount}
              />
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}

export default SideNav;
